import React from 'react';
import { FaSearch, FaEraser, FaSlidersH } from 'react-icons/fa';

export interface GridFilterSelectOption {
  value: string;
  label: string;
}

export interface GridFiltersBarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  searchPlaceholder: string;
  searchAriaLabel: string;
  filterValue: string;
  onFilterChange: (value: string) => void;
  filterAriaLabel: string;
  filterEmptyLabel: string;
  filterOptions: GridFilterSelectOption[];
  includeEmptyOption?: boolean;
  totalShowing: number;
  showAdvanced: boolean;
  onToggleAdvanced: () => void;
  hasActiveFilters: boolean;
  onClear: () => void;
  viewToggle?: React.ReactNode;
}

const GridFiltersBar = ({
  searchQuery,
  onSearchChange,
  onSearch,
  searchPlaceholder,
  searchAriaLabel,
  filterValue,
  onFilterChange,
  filterAriaLabel,
  filterEmptyLabel,
  filterOptions,
  includeEmptyOption = true,
  totalShowing,
  showAdvanced,
  onToggleAdvanced,
  hasActiveFilters,
  onClear,
  viewToggle,
}: GridFiltersBarProps) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const countLabel = `Mostrando ${totalShowing} ${totalShowing === 1 ? 'registro' : 'registros'}`;

  return (
    <div className="user-filters">
      <div className="user-filters__primary">
        <div className="user-filters__search-wrap">
          <FaSearch className="user-filters__search-icon" size={18} aria-hidden />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            className="user-filters__search-input"
            aria-label={searchAriaLabel}
          />
        </div>
        <button
          type="button"
          className="user-filters__search-btn"
          onClick={onSearch}
          title="Pesquisar"
        >
          <FaSearch size={16} aria-hidden />
          <span className="user-filters__search-btn-label">Buscar</span>
        </button>
      </div>

      <div className="user-filters__secondary">
        {viewToggle ? (
          <div className="user-filters__filter-row">
            <div className="user-filters__select-wrap">
              <FaSlidersH className="user-filters__select-icon" size={16} aria-hidden />
              <select
                value={filterValue}
                onChange={(e) => onFilterChange(e.target.value)}
                className="user-filters__select"
                aria-label={filterAriaLabel}
              >
                {includeEmptyOption ? <option value="">{filterEmptyLabel}</option> : null}
                {filterOptions.map((opt) => (
                  <option key={opt.value || '__all__'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="user-filters__view-toggle-wrap">{viewToggle}</div>
          </div>
        ) : (
          <div className="user-filters__select-wrap">
            <FaSlidersH className="user-filters__select-icon" size={16} aria-hidden />
            <select
              value={filterValue}
              onChange={(e) => onFilterChange(e.target.value)}
              className="user-filters__select"
              aria-label={filterAriaLabel}
            >
              {includeEmptyOption ? <option value="">{filterEmptyLabel}</option> : null}
              {filterOptions.map((opt) => (
                <option key={opt.value || '__all__'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="user-filters__toolbar">
          <button
            type="button"
            className={`user-filters__advanced-btn ${showAdvanced ? 'user-filters__advanced-btn--active' : ''}`}
            onClick={onToggleAdvanced}
            title="Filtros avançados"
            aria-expanded={showAdvanced}
          >
            <FaSlidersH size={16} aria-hidden />
            <span className="user-filters__toolbar-label">Avançado</span>
          </button>

          {hasActiveFilters && (
            <button
              type="button"
              className="user-filters__clear-btn"
              onClick={onClear}
              title="Limpar busca e filtros"
            >
              <FaEraser size={16} aria-hidden />
              <span className="user-filters__toolbar-label">Limpar</span>
            </button>
          )}
        </div>
      </div>

      <div className="user-filters__count" role="status">
        {countLabel}
      </div>
    </div>
  );
};

export default GridFiltersBar;
